import { inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { clamp, round, uniqBy } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { isUndefined } from 'lodash';
import { buffer, filter, merge, NEVER } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { getOutputByName } from '../../../../core/h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../../../core/h3ml/state/task-workspace-store';
import { RootKeyboardShortcutService } from '../../../../core/shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { DebouncedEventTracker } from '../../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { KeyboardShortcutName } from '../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import {
  isOrthographicVideoOutput,
  isPointCloudOutput,
  isVideoOutput,
} from '../../../../cross-team-shared/misc-utils/output-utils';
import {
  DEFAULT_BRIGHTNESS,
  DEFAULT_CONTRAST,
} from '../../../../cross-team-shared/state/layer-settings-state-2d';
import { settingsStoreToken } from '../../../../cross-team-shared/state/setting-state';
import { EventTrackingService } from '../../../../shared/event-tracking/event-tracking.service';
import { Output } from '../../../../team-apis/otterdam/outputs/output';
import {
  defaultSettingConfig,
  SliderSettingConfig,
} from './settings-panel/slider-settings-config';

interface ShortcutConfig {
  settingName: string;
  settingConfig: SliderSettingConfig;
  settingStore: Store<number>;
  decreaseShortcutName: KeyboardShortcutName;
  increaseShortcutName: KeyboardShortcutName;
  defaultValue?: number;
}

@Injectable()
export class SettingsKeyboardShortcutsService extends InjectableSuperclass {
  private keyboardShortcutService = inject(RootKeyboardShortcutService);
  private eventTrackingService = inject(EventTrackingService);
  private settingsStore = inject(settingsStoreToken);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);

  private opacity3dConfig: ShortcutConfig = {
    settingName: 'opacity',
    settingConfig: defaultSettingConfig,
    settingStore: this.settingsStore('layerSettings3d')('opacity'),
    decreaseShortcutName: 'decreaseOpacity',
    increaseShortcutName: 'increaseOpacity',
  };
  private opacity2dConfig: ShortcutConfig = {
    settingName: 'opacity',
    settingConfig: defaultSettingConfig,
    settingStore: this.settingsStore('layerSettings2d')('opacity'),
    decreaseShortcutName: 'decreaseOpacity',
    increaseShortcutName: 'increaseOpacity',
  };
  private brightnessConfig: ShortcutConfig = {
    settingName: 'brightness',
    settingConfig: defaultSettingConfig,
    settingStore: this.settingsStore('layerSettings2d')('brightness'),
    decreaseShortcutName: 'decreaseBrightness',
    increaseShortcutName: 'increaseBrightness',
    defaultValue: DEFAULT_BRIGHTNESS,
  };
  private contrastConfig: ShortcutConfig = {
    settingName: 'contrast',
    settingConfig: defaultSettingConfig,
    settingStore: this.settingsStore('layerSettings2d')('contrast'),
    decreaseShortcutName: 'decreaseContrast',
    increaseShortcutName: 'increaseContrast',
    defaultValue: DEFAULT_CONTRAST,
  };

  register() {
    this.unsubscribe();

    this.setUpSubscriptionToChangeSettings(
      this.opacity3dConfig,
      isPointCloudOutput,
    );
    this.setUpSubscriptionToChangeSettings(this.opacity2dConfig, isVideoOutput);
    this.setUpSubscriptionToChangeSettings(
      this.brightnessConfig,
      (output) => isVideoOutput(output) && !isOrthographicVideoOutput(output),
    );
    this.setUpSubscriptionToChangeSettings(
      this.contrastConfig,
      (output) => isVideoOutput(output) && !isOrthographicVideoOutput(output),
    );
  }

  private setUpSubscriptionToChangeSettings(
    config: ShortcutConfig,
    outputPredicate: (output: Output) => boolean,
  ) {
    const eventTracker = this.createDebouncedEventTracker(config);

    this.subscribeTo(
      merge(
        this.getValueFromIncreaseOrDecreaseEvent$(config),
        this.getValueFromResetEvent$(config).pipe(
          tap(() => {
            eventTracker.addBatchContext({ batch_included_reset: true });
          }),
        ),
      ).pipe(
        filter(() => {
          const { project, selectedOutputName } =
            this.taskWorkspaceStore.state();
          return outputPredicate(getOutputByName(project, selectedOutputName));
        }),
      ),
      (value: number) => {
        const newValue = clampValue(value, config.settingConfig);
        config.settingStore.set(newValue);
        eventTracker.trackWithDebounce({
          source: 'keyboard_shortcut',
          value,
        });
      },
    );
  }

  private createDebouncedEventTracker(config: ShortcutConfig) {
    const eventTracker = new DebouncedEventTracker(
      {
        id: 'adjust_view_setting',
        bucket: 'navigation',
        sub_bucket: 'frame',
        method: config.settingName,
      },
      this.eventTrackingService,
    );
    eventTracker.addContext({ batch_included_reset: false });
    return eventTracker;
  }

  private getValueFromResetEvent$(config: ShortcutConfig) {
    if (isUndefined(config.defaultValue)) {
      return NEVER;
    }

    return this.getShortcuts$(config).pipe(
      // Add a 10ms coz it gets tricky to trigger if it is 0
      buffer(this.getShortcuts$(config).pipe(debounceTime(10))),
      filter(
        (shortcuts) =>
          uniqBy(shortcuts, (shortcut) => shortcut.shortcutName).length === 2,
      ),
      map(() => config.defaultValue as number),
    );
  }

  private getValueFromIncreaseOrDecreaseEvent$(config: ShortcutConfig) {
    return this.getShortcuts$(config).pipe(
      map((event) => {
        const offset =
          event.shortcutName === config.decreaseShortcutName ? -5 : 5;
        return config.settingStore.state() + offset;
      }),
    );
  }

  private getShortcuts$(config: ShortcutConfig) {
    return merge(
      this.keyboardShortcutService.getEvent$(config.decreaseShortcutName),
      this.keyboardShortcutService.getEvent$(config.increaseShortcutName),
    );
  }
}

function clampValue(newValue: number, config: SliderSettingConfig) {
  const stepPrecision = Math.abs(Math.log10(config.step));

  const lowerBound = round(config.min, stepPrecision);
  const upperBound = round(config.max, stepPrecision);

  return clamp(newValue, lowerBound, upperBound);
}
